import React, { useState, useEffect, HTMLAttributes, useRef } from 'react';
import '../../base.scss';
import './Autocomplete.scss';
import './MultiAutocomplete.scss';
import useOutsideClick from '../../utilities/hooks/use-outside-click';
import { classNames } from '../../utilities';
import { Icon } from '../Icon';
import { Input } from '../Input';
import { Button } from '../Button';
import { AutocompleteDropdown } from './AutocompleteDropdown';
import { Pill } from '../Pill/Pill';

export interface MultiAutocompleteProps extends HTMLAttributes<HTMLElement> {
  /** required for accessibility if no labels */
  'aria-label'?: string;
  /** CSS class name to apply */
  className?: string;
  /** Whether input should be disabled */
  disabled?: boolean;
  /** When true, the user must select from the list of suggestions */
  forceSelect?: boolean;
  /** Input HTML id attribute */
  id?: string;
  /** Whether the autocomplete should display loading state */
  isLoading?: boolean;
  /** Optional limit on the number of user selections allowed */
  maxSelections?: number;
  /** Input HTML name attribute */
  name?: string;
  /** Callback to retrieve the Input value as the user types */
  onInputChange?: (input: string) => Promise<void> | void;
  /** Callback when user adds a selection */
  onSelectionChange?: (selection: string[]) => void;
  /** Callback when user clears the Autocomplete */
  onClear?: () => void;
  /** Input placeholder attribute */
  placeholder?: string;
  /** List of autocomplete suggestions, to display in dropdown */
  suggestions?: string[];
  /** List of user selections */
  selections: string[];
  /** The Input value */
  value?: string;
}

/**
 * A controlled, multiselect Autocomplete input. For a single-select variant,
 * see Autocomplete.tsx
 * 
 * Dropdown/suggestion display is based on the following rules:
 * 
 * - inactive -> no dropdown
 * - active && undefined suggestions -> no dropdown
 * - active && suggestions -> dropdown w/ suggestions
 * - active && isLoading -> dropdown w/ 'loading' message
 * - active && !isLoading && no suggestions -> dropdown w/ 'no results' message
 * - active && !isLoading && suggestions -> dropdown w/ suggestions
 */
export function MultiAutocomplete(props: MultiAutocompleteProps) {
  const { 
    className = '', 
    disabled = false,
    forceSelect = false,
    id,  
    isLoading, 
    maxSelections = 0,
    name,
    onInputChange,
    onSelectionChange,
    onClear,
    placeholder,
    selections = [],
    suggestions,
    value
  } = props;

  const loadingMessage = 'Loading results...';
  const noMatchesMessage = 'No matched results. Please try again';
  const hasSuggestions = suggestions && suggestions.length > 0;

  let displayedMessage = '';
  if (isLoading) {
    displayedMessage = loadingMessage;
  } else if (!hasSuggestions && forceSelect) {
    displayedMessage = noMatchesMessage;
  }

  let suggestionsHint;
  if (!value && hasSuggestions) {
    suggestionsHint = 'Suggested';
  }

  const [isActive, setIsActive] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [focusedSuggestion, setFocusedSuggestion] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // using hook instead of onBlur because if user uses keyboard shortcut
  //  from one suggestion to another, that raises onBlur.
  // Remove focus from active dropdown on outside click
  useOutsideClick(containerRef, () => {
    if (showDropdown) {
      clearFocused();
    }
  });

  useEffect(() => {
    if (isActive) {
      if (!isLoading && suggestions === undefined) {
        // Hide the dropdown if suggestions are undefined and we aren't loading them yet.
        setShowDropdown(false);
      } else {
        setShowDropdown(true);
      }
    } else {
      // Hide the dropdown always if Autocomplete isn't active.
      setShowDropdown(false);
    }
  }, [suggestions, isLoading, isActive]);

  const clearFocused = () => setFocusedSuggestion(-1);

  const canSelect = () => maxSelections <= 0 || selections.length < maxSelections;

  const updateValue = (newVal: string) => {
    if (onInputChange) {
      onInputChange(newVal);
    }
    clearFocused();
  }

  const addSuggestionAtIndex = (suggestionIndex: number) => {
    if (suggestions && suggestions[suggestionIndex]) {
      addSelected(suggestions[suggestionIndex]);
    }
  }

  const addSelected = (val?: string) => {
    if (canSelect() && onSelectionChange && val) {
      onSelectionChange(selections.concat(val));
    }

    setShowDropdown(false);
    clearFocused();
    inputRef.current?.focus();
  }

  const onRemove = (selectionToRemove: string) => {
    if (onSelectionChange) {
      const newSelections = selections.filter((selection) => selection != selectionToRemove);
      onSelectionChange(newSelections);
    }
    setShowDropdown(false);
    clearFocused();
  }

  const onKeyDown = (evt: React.KeyboardEvent) => {
    if (isActive) {
      switch (evt.keyCode) {
        case 13:
          // Enter
          // Handle situations when use hits enter on their own input
          if (focusedSuggestion >= 0) {
            addSuggestionAtIndex(focusedSuggestion);
          } else if (forceSelect) {
            if (suggestions && suggestions.length > 0) {
              addSuggestionAtIndex(0);
            }
            // otherwise, per requirements, nothing should happen.
          } else {
            // nothing focused, no forced selection -> submit user input
            addSelected(value);
          }
          break;
        case 32:
          // Spacebar
          if (showDropdown && suggestions && focusedSuggestion >= 0) {
            // Only allow spacebar submit if there's a focused suggestion.
            // So not when focus is on the input.
            addSuggestionAtIndex(focusedSuggestion);
            evt.preventDefault();
          }
          break;
        case 38:
          // Up arrow
          if (showDropdown && suggestions) {
            setFocusedSuggestion(Math.max(0, focusedSuggestion - 1));
            evt.preventDefault();
          }
          break;
        case 40:
          // Down arrow
          if (showDropdown && suggestions) {
            setFocusedSuggestion(Math.min(suggestions.length - 1, focusedSuggestion + 1));
            evt.preventDefault();
          }
          break;
      }
    }
  }
  
  const pills = selections.map((selection: string) => (
    <Pill key={selection} text={selection} disabled={disabled} onClose={onRemove}></Pill>
  ));

  const inputContainerClasses = classNames(
    'ps-multiautocomplete__input-container', 
    forceSelect ? 'ps-multiautocomplete__input-container--no-search' : '',
    disabled ? 'ps-multiautocomplete__input-container--disabled' : ''
  );

  const dropdownClasses = classNames(
    forceSelect ? 'ps-multiautocomplete__dropdown--no-search' : ''
  );

  return (
    <div 
      ref={containerRef} 
      className={`ps-multiautocomplete ${className}`} 
      onFocus={() => setIsActive(true)}
      onKeyDown={(evt: React.KeyboardEvent) => onKeyDown(evt)}
    >
      <div className={inputContainerClasses}>
        {!forceSelect && <Icon name='Search' className='ps-autocomplete__input-icon ps-autocomplete__search-icon'/>}
        <div className='ps-multiautocomplete__pills-and-search'>
          {pills}
          <Input
            id={id}
            name={name}
            value={value}
            placeholder={placeholder}
            aria-label={props['aria-label']}
            onChange={evt => updateValue(evt.target.value)}
            onKeyDown={evt => { if (evt.keyCode === 8 && !value) { onRemove(selections[selections.length - 1]); } }}
            disabled={disabled}
            ref={inputRef}
          />
        </div>
        {!disabled && (value || selections.length > 0) && <Button
          onClick={() => onClear && onClear()}
          variant='icon-only'
          icon='Close'
          className='ps-autocomplete__close-icon' 
          aria-label='Clear input'
        />}
      </div>
      <AutocompleteDropdown 
        suggestions={suggestions}
        suggestionsHint={suggestionsHint}
        onSelectSuggestion={addSuggestionAtIndex}
        onFocusSuggestion={setFocusedSuggestion}
        message={displayedMessage}
        focusedSuggestion={focusedSuggestion}
        isOpen={showDropdown}
        isLoading={isLoading}
        highlightedSuggestion={forceSelect && focusedSuggestion < 0 ? 0 : -1} 
        className={dropdownClasses}
      />
    </div>
  );
}